/* eslint-disable */

import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, TextField, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import YarnCrawler from './YarnCrawler'
import YarnLibrary from './YarnLibrary'
import YarnFormDialog, { YarnFormData } from './YarnFormDialog'
import ColorwayFormDialog, { ColorwayFormData } from './ColorwayFormDialog'
import UserYarnFormDialog, { UserYarnFormData } from './UserYarnFormDialog'
import SelectColorwayDialog from './SelectColorwayDialog'
import { parseYarnProfile } from '../utils/yarnParsing'
import { api } from '../requests/axios'

interface Yarn {
  id: number
  name: string
  brand: string
  length: number
  weight: number
  material: string
  length_unit: string
  weight_unit: string
}

interface Colorway {
  id: number
  name: string
  hex_color: string
  hex_colors?: string[]
  hex_color_locked: boolean
  yarn_id: number
  yarn?: Yarn
}

interface UserYarn {
  id: number
  colorway_id: number
  dye_lot: string
  quantity: number
  colorway?: Colorway
}

interface PaginationData {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

export default function Yarns() {
  const [userYarns, setUserYarns] = useState<UserYarn[]>([])
  const [loading, setLoading] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)
  const [pagination, setPagination] = useState<PaginationData | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  
  // Add yarn flow states
  const [showYarnCrawler, setShowYarnCrawler] = useState(false)
  const [showYarnForm, setShowYarnForm] = useState(false)
  const [showColorwayForm, setShowColorwayForm] = useState(false)
  const [showUserYarnForm, setShowUserYarnForm] = useState(false)
  
  const [yarnFormData, setYarnFormData] = useState<YarnFormData>({
    name: '',
    brand: '',
    length: '',
    weight: '',
    material: '',
    length_unit: 'm',
    weight_unit: 'g',
    gauge_sts: undefined,
    gauge_rows: undefined,
    gauge_knitting_needle_id: undefined,
    gauge_needle_mm: undefined,
    gauge_needle_us: undefined
  })
  
  const [colorwayFormData, setColorwayFormData] = useState<ColorwayFormData>({
    name: '',
    hex_color: '#000000',
    hex_color_locked: false,
    hex_colors: ['#000000']
  })
  
  const [userYarnFormData, setUserYarnFormData] = useState<UserYarnFormData>({
    colorway_id: 0,
    dye_lot: '',
    quantity: 1
  })
  
  const [selectedYarnId, setSelectedYarnId] = useState<number | null>(null)
  const [selectedColorwayId, setSelectedColorwayId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedColorwayForDyeLot, setSelectedColorwayForDyeLot] = useState<number | null>(null)
  const [showAddColorwayDialog, setShowAddColorwayDialog] = useState(false)
  const [showSelectColorwayDialog, setShowSelectColorwayDialog] = useState(false)
  const [selectedYarnForColorway, setSelectedYarnForColorway] = useState<number | null>(null)
  const [recentlyCreatedColorwayId, setRecentlyCreatedColorwayId] = useState<number | null>(null)
  const [yarnCrawlerSelections, setYarnCrawlerSelections] = useState<{ brand: string; yarn: string; profile: string[]; imageUrl?: string | null } | null>(null)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  useEffect(() => {
    // Reset to page 1 when search changes (this will trigger the fetch via currentPage change)
    if (searchQuery !== '') {
      setCurrentPage(1)
    }
  }, [searchQuery])

  useEffect(() => {
    fetchUserYarns()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery])

  const getCurrentUserId = async (): Promise<number | null> => {
    if (userId) return userId
    
    // Try to get user_id from localStorage first (if stored during login)
    const storedUserId = localStorage.getItem('user_id')
    if (storedUserId) {
      const id = parseInt(storedUserId)
      if (!isNaN(id)) {
        setUserId(id)
        return id
      }
    }
    
    // Fetch from current user endpoint
    try {
      const userResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/users/current.json`)
      if (userResponse && userResponse.data && userResponse.data.id) {
        const id = userResponse.data.id
        setUserId(id)
        localStorage.setItem('user_id', id.toString())
        return id
      }
    } catch (e: any) {
      console.error('Error fetching current user:', e)
      if (e && e.response && e.response.status === 401) {
        setError('Please log in to view your yarn library')
      }
    }
    
    return null
  }

  const fetchUserYarns = async () => {
    setLoading(true)
    try {
      const email = localStorage.getItem('email')
      if (!email) {
        setError('Please log in to view your yarn library')
        return
      }
      
      const currentUserId = await getCurrentUserId()
      if (!currentUserId) {
        setError('Unable to determine user ID. Please log out and log back in.')
        return
      }
      
      const params: any = { page: currentPage }
      if (searchQuery) {
        params.search = searchQuery
      }
      
      const response = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/users/${currentUserId}/user_yarns.json`, {
        params
      })
      
      // Handle pagination metadata
      if (response.data.pagination) {
        setPagination(response.data.pagination)
      }
      
      // Convert new structure (yarns with nested colorways and user_yarns) to flat user_yarns array
      const yarnsData = response.data.yarns || response.data || []
      const flatUserYarns: UserYarn[] = []
      
      yarnsData.forEach((yarnData: any) => {
        const yarn = yarnData.yarn
        const colorways = yarnData.colorways || []
        const userYarns = yarnData.user_yarns || []
        
        // Create a colorway lookup map
        const colorwayMap = new Map()
        colorways.forEach((cw: Colorway) => {
          colorwayMap.set(cw.id, { ...cw, yarn })
        })
        
        // Map user_yarns with their colorways
        userYarns.forEach((uy: any) => {
          flatUserYarns.push({
            ...uy,
            colorway: colorwayMap.get(uy.colorway_id)
          })
        })
      })
      
      setUserYarns(flatUserYarns)
    } catch (error: any) {
      console.error('Error fetching user yarns:', error)
      if (error && error.response && error.response.status === 404) {
        localStorage.removeItem('user_id')
        setError('User not found. Please log out and log back in.')
      } else {
        setError((error && error.response && error.response.data && error.response.data.error) || 'Failed to load yarn library')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddYarn = () => {
    setShowYarnCrawler(true)
  }

  const handleYarnCrawlerSelect = async (brand: string, yarn: string, profile: string[], imageUrl?: string | null) => {
    // Store selections for when user goes back
    setYarnCrawlerSelections({ brand, yarn, profile, imageUrl })
    // Parse fresh data from YarnCrawler - this ensures we always get clean data
    const parsedData = parseYarnProfile(profile, brand, yarn)
    
    setError(null)
    try {
      // Check if yarn already exists
      const checkResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns.json`)
      const existingYarns: Yarn[] = Array.isArray(checkResponse.data) ? checkResponse.data : []
      const existingYarn = existingYarns.find(
        (y: Yarn) => y.name.toLowerCase() === parsedData.name.toLowerCase() && 
                     y.brand.toLowerCase() === parsedData.brand.toLowerCase()
      )
      
      if (existingYarn) {
        // Yarn already exists - skip form and go straight to colorway selection
        setSelectedYarnForColorway(existingYarn.id)
        setShowYarnCrawler(false)
        setShowSelectColorwayDialog(true)
      } else {
        // Yarn doesn't exist - show form to create it
        setYarnFormData({
          name: parsedData.name || '',
          brand: parsedData.brand || '',
          length: parsedData.length || '',
          weight: parsedData.weight || '',
          material: parsedData.material || '',
          length_unit: parsedData.length_unit || 'm',
          weight_unit: parsedData.weight_unit || 'g',
          gauge_sts: parsedData.gauge_sts,
          gauge_rows: parsedData.gauge_rows,
          gauge_knitting_needle_id: undefined, // Reset needle selection
          gauge_needle_mm: parsedData.gauge_needle_mm,
          gauge_needle_us: parsedData.gauge_needle_us
        })
        setShowYarnCrawler(false)
        setShowYarnForm(true)
      }
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to check for existing yarn'
      setError(errorMsg)
      // Fallback to showing form if check fails
      setYarnFormData({
        name: parsedData.name || '',
        brand: parsedData.brand || '',
        length: parsedData.length || '',
        weight: parsedData.weight || '',
        material: parsedData.material || '',
        length_unit: parsedData.length_unit || 'm',
        weight_unit: parsedData.weight_unit || 'g',
        gauge_sts: parsedData.gauge_sts,
        gauge_rows: parsedData.gauge_rows,
        gauge_knitting_needle_id: undefined,
        gauge_needle_mm: parsedData.gauge_needle_mm,
        gauge_needle_us: parsedData.gauge_needle_us
      })
      setShowYarnCrawler(false)
      setShowYarnForm(true)
    }
  }

  const handleYarnFormSubmit = async () => {
    setError(null)
    try {
      // Check if yarn already exists
      const checkResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns.json`)
      const existingYarns: Yarn[] = Array.isArray(checkResponse.data) ? checkResponse.data : []
      const existingYarn = existingYarns.find(
        (y: Yarn) => y.name.toLowerCase() === yarnFormData.name.toLowerCase() && 
                     y.brand.toLowerCase() === yarnFormData.brand.toLowerCase()
      )
      
      let yarnId: number
      if (existingYarn) {
        yarnId = existingYarn.id
      } else {
        // Create new yarn
        try {
          const createResponse = await api.post(`${process.env.REACT_APP_API_PROXY_URL}/yarns.json`, {
            yarn: yarnFormData
          })
          yarnId = createResponse.data.yarn.id
        } catch (createError: any) {
          // If creation fails because yarn already exists, try to find it
          if (createError && createError.response && createError.response.data && createError.response.data.errors && createError.response.data.errors.includes('Yarn already exists.')) {
            const retryResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns.json`)
            const retryYarns: Yarn[] = Array.isArray(retryResponse.data) ? retryResponse.data : []
            const foundYarn = retryYarns.find(
              (y: Yarn) => y.name.toLowerCase() === yarnFormData.name.toLowerCase() && 
                           y.brand.toLowerCase() === yarnFormData.brand.toLowerCase()
            )
            if (foundYarn) {
              yarnId = foundYarn.id
            } else {
              throw createError
            }
          } else {
            throw createError
          }
        }
      }
      
      // Create gauge if gauge data is provided
      if (yarnFormData.gauge_sts && yarnFormData.gauge_rows && yarnFormData.gauge_knitting_needle_id) {
        try {
          await api.post(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${yarnId}/gauges.json`, {
            gauge: {
              sts: parseInt(yarnFormData.gauge_sts),
              rows: parseInt(yarnFormData.gauge_rows),
              knitting_needle_id: yarnFormData.gauge_knitting_needle_id
            }
          })
        } catch (gaugeError: any) {
          // Log gauge error but don't fail the whole flow
          console.error('Error creating gauge:', gaugeError)
          // Optionally show a warning, but continue
        }
      }
      
      setSelectedYarnId(yarnId)
      setShowYarnForm(false)
      setShowColorwayForm(true)
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) || 
                       (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to create yarn'
      setError(errorMsg)
    }
  }

  const handleColorwayFormSubmit = async () => {
    if (!selectedYarnId) return
    
    setError(null)
    try {
      // Check if colorway already exists for this yarn
      const checkResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnId}/colorways.json`)
      const existingColorways: Colorway[] = checkResponse.data || []
      const formColors = colorwayFormData.hex_colors?.length ? colorwayFormData.hex_colors : [colorwayFormData.hex_color]
      const existingColorway = existingColorways.find((c: Colorway) => {
        const cColors = (c as any).hex_colors?.length ? (c as any).hex_colors : [c.hex_color]
        return c.name.toLowerCase() === colorwayFormData.name.toLowerCase() &&
          JSON.stringify(formColors) === JSON.stringify(cColors)
      })
      
      let colorwayId: number
      if (existingColorway) {
        colorwayId = existingColorway.id
      } else {
        const createResponse = await api.post(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnId}/colorways.json`, {
          colorway: {
            name: colorwayFormData.name,
            hex_colors: formColors,
            is_multicolored: formColors.length > 1
          }
        })
        colorwayId = createResponse.data.id
      }
      
      setSelectedColorwayId(colorwayId)
      setUserYarnFormData({ ...userYarnFormData, colorway_id: colorwayId })
      setShowColorwayForm(false)
      setShowUserYarnForm(true)
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) || 
                       (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to create colorway'
      setError(errorMsg)
    }
  }

  const handleUserYarnFormSubmit = async () => {
    const colorwayId = selectedColorwayId ?? selectedColorwayForDyeLot ?? userYarnFormData.colorway_id
    if (!colorwayId) return

    const currentUserId = userId ?? (await getCurrentUserId())
    if (!currentUserId) {
      setError('Please log in to add yarn to your library')
      return
    }

    setError(null)
    try {
      // Check if user_yarn already exists
      const existingUserYarns = userYarns.filter(
        (uy: UserYarn) => uy.colorway_id === colorwayId &&
                          uy.dye_lot === userYarnFormData.dye_lot
      )
      
      if (existingUserYarns.length > 0) {
        setError('This yarn is already in your library')
        return
      }
      
      // Create user_yarn
      await api.post(`${process.env.REACT_APP_API_PROXY_URL}/users/${currentUserId}/user_yarns.json`, {
        user_yarn: {
          colorway_id: colorwayId,
          dye_lot: userYarnFormData.dye_lot,
          quantity: userYarnFormData.quantity
        }
      })
      
      // Reset forms and close dialogs
      setShowUserYarnForm(false)
      setYarnFormData({
        name: '',
        brand: '',
        length: '',
        weight: '',
        material: '',
        length_unit: 'm',
        weight_unit: 'g',
        gauge_sts: undefined,
        gauge_rows: undefined,
        gauge_knitting_needle_id: undefined,
        gauge_needle_mm: undefined,
        gauge_needle_us: undefined
      })
      setColorwayFormData({
        name: '',
        hex_color: '#000000',
        hex_color_locked: false,
        hex_colors: ['#000000']
      })
      setUserYarnFormData({
        colorway_id: 0,
        dye_lot: '',
        quantity: 1
      })
      setSelectedYarnId(null)
      setSelectedColorwayId(null)
      setSelectedColorwayForDyeLot(null)
      setSelectedYarnForColorway(null)
      
      // Refresh yarn library - reset to first page after adding
      setCurrentPage(1)
      await fetchUserYarns()
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) || 
                       (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to add yarn to library'
      setError(errorMsg)
    }
  }

  const handleUpdateUserYarn = async (id: number, quantity: number) => {
    if (!userId) return
    
    try {
      await api.patch(`${process.env.REACT_APP_API_PROXY_URL}/users/${userId}/user_yarns/${id}.json`, {
        user_yarn: {
          quantity: quantity
        }
      })
      // Update local state instead of refetching to avoid flickering
      setUserYarns(prevYarns => 
        prevYarns.map(yarn => 
          yarn.id === id ? { ...yarn, quantity } : yarn
        )
      )
    } catch (error: any) {
      setError((error && error.response && error.response.data && error.response.data.error) || 'Failed to update yarn quantity')
      throw error // Re-throw so YarnCard can handle the error
    }
  }

  const handleAddColorway = (yarnId: number) => {
    setSelectedYarnForColorway(yarnId)
    // Get existing colorway IDs for this yarn from user's library
    const existingColorwayIds = userYarns
      .filter(uy => uy.colorway && uy.colorway.yarn && uy.colorway.yarn.id === yarnId)
      .map(uy => uy.colorway!.id)
    
    // Check if there are any existing colorways for this yarn
    setShowSelectColorwayDialog(true)
  }

  const handleSelectExistingColorway = (colorwayId: number) => {
    // User selected an existing colorway, open user yarn form dialog
    setSelectedColorwayForDyeLot(colorwayId)
    setUserYarnFormData({
      colorway_id: colorwayId,
      dye_lot: '',
      quantity: 1
    })
    setShowSelectColorwayDialog(false)
    setShowUserYarnForm(true)
  }

  const handleCreateNewColorway = () => {
    // Close SelectColorwayDialog and open Add Colorway dialog
    // Don't clear selectedYarnForColorway - we need it for creating the colorway
    setShowSelectColorwayDialog(false)
    setColorwayFormData({
      name: '',
      hex_color: '#000000',
      hex_color_locked: false,
      hex_colors: ['#000000']
    })
    setShowAddColorwayDialog(true)
    // Clear yarn crawler selections since we're creating a new colorway
    setYarnCrawlerSelections(null)
  }

  const handleAddColorwaySubmit = async () => {
    if (!selectedYarnForColorway) return
    
    setError(null)
    try {
      let colorwayId: number
      
      // If we're editing a recently created colorway, update it instead of creating a new one
      if (recentlyCreatedColorwayId) {
        const formColors = colorwayFormData.hex_colors?.length ? colorwayFormData.hex_colors : [colorwayFormData.hex_color]
        const updateResponse = await api.patch(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnForColorway}/colorways/${recentlyCreatedColorwayId}.json`, {
          colorway: {
            name: colorwayFormData.name,
            hex_colors: formColors,
            is_multicolored: formColors.length > 1
          }
        })
        colorwayId = updateResponse.data.id
      } else {
        // Check if colorway already exists for this yarn
        const checkResponse = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnForColorway}/colorways.json`)
        const existingColorways: Colorway[] = checkResponse.data || []
        const formColors = colorwayFormData.hex_colors?.length ? colorwayFormData.hex_colors : [colorwayFormData.hex_color]
        const existingColorway = existingColorways.find((c: Colorway) => {
          const cColors = (c as any).hex_colors?.length ? (c as any).hex_colors : [c.hex_color]
          return c.name.toLowerCase() === colorwayFormData.name.toLowerCase() &&
            JSON.stringify(formColors) === JSON.stringify(cColors)
        })
        
        if (existingColorway) {
          colorwayId = existingColorway.id
        } else {
          const createResponse = await api.post(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnForColorway}/colorways.json`, {
            colorway: {
              name: colorwayFormData.name,
              hex_colors: formColors,
              is_multicolored: formColors.length > 1
            }
          })
          colorwayId = createResponse.data.id
          setRecentlyCreatedColorwayId(colorwayId) // Track that we just created this colorway
        }
      }
      
      // After creating/updating colorway, open dialog to add to library
      setSelectedColorwayForDyeLot(colorwayId)
      setUserYarnFormData({
        colorway_id: colorwayId,
        dye_lot: '',
        quantity: 1
      })
      setShowAddColorwayDialog(false)
      setShowUserYarnForm(true)
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) || 
                       (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to create/update colorway'
      setError(errorMsg)
    }
  }

  const handleAddDyeLot = async (colorwayId: number, dyeLot: string, quantity: number) => {
    if (!userId) return
    
    setError(null)
    try {
      await api.post(`${process.env.REACT_APP_API_PROXY_URL}/users/${userId}/user_yarns.json`, {
        user_yarn: {
          colorway_id: colorwayId,
          dye_lot: dyeLot,
          quantity: quantity
        }
      })
      
      // Refresh data but keep user on current page so modal stays open
      await fetchUserYarns()
    } catch (error: any) {
      const errorMsg = (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0]) || 
                       (error && error.response && error.response.data && error.response.data.error) || 
                       'Failed to add dye lot'
      setError(errorMsg)
      throw error // Re-throw so the component can handle it
    }
  }

  const handleBackToColorwayEdit = async () => {
    if (recentlyCreatedColorwayId && selectedYarnForColorway) {
      // Fetch the colorway we just created to populate the form
      try {
        const response = await api.get(`${process.env.REACT_APP_API_PROXY_URL}/yarns/${selectedYarnForColorway}/colorways/${recentlyCreatedColorwayId}.json`)
        const colorway = response.data
        setColorwayFormData({
          name: colorway.name,
          hex_color: colorway.hex_color,
          hex_color_locked: colorway.hex_color_locked || false,
          hex_colors: (colorway as any).hex_colors?.length ? (colorway as any).hex_colors : [colorway.hex_color]
        })
        setShowAddColorwayDialog(true)
      } catch (error: any) {
        console.error('Error fetching colorway:', error)
        // Fallback: go back to selection
        setShowSelectColorwayDialog(true)
      }
    } else {
      // Go back to colorway selection
      setShowSelectColorwayDialog(true)
    }
  }

  const handleDeleteUserYarn = async (id: number) => {
    if (!userId) return
    if (!window.confirm('Are you sure you want to remove this yarn from your library?')) return
    
    try {
      await api.delete(`${process.env.REACT_APP_API_PROXY_URL}/users/${userId}/user_yarns/${id}.json`)
      // Refresh current page (may need to go back if current page becomes empty)
      await fetchUserYarns()
    } catch (error: any) {
      setError((error && error.response && error.response.data && error.response.data.error) || 'Failed to delete yarn')
    }
  }

  const resetForms = () => {
    setYarnFormData({
      name: '',
      brand: '',
      length: '',
      weight: '',
      material: '',
      length_unit: 'm',
      weight_unit: 'g',
      gauge_sts: undefined,
      gauge_rows: undefined,
      gauge_knitting_needle_id: undefined,
      gauge_needle_mm: undefined,
      gauge_needle_us: undefined
    })
    setColorwayFormData({
      name: '',
      hex_color: '#000000',
      hex_color_locked: false,
      hex_colors: ['#000000']
    })
    setUserYarnFormData({
      colorway_id: 0,
      dye_lot: '',
      quantity: 1
    })
    setSelectedYarnId(null)
    setSelectedColorwayId(null)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          My Yarn Library
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, maxWidth: 400 }}>
          <TextField
            placeholder="Search by brand, name, or material..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Button variant="contained" onClick={handleAddYarn}>
          Add Yarn
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <YarnLibrary 
        userYarns={userYarns} 
        loading={loading} 
        onDelete={handleDeleteUserYarn}
        onUpdate={handleUpdateUserYarn}
        onAddDyeLot={handleAddDyeLot}
        onAddColorway={handleAddColorway}
        pagination={pagination || undefined}
        onPageChange={handlePageChange}
        searchQuery={searchQuery}
      />

      {/* Yarn Crawler Dialog */}
      <Dialog open={showYarnCrawler} onClose={() => {
        setShowYarnCrawler(false)
        setYarnCrawlerSelections(null) // Clear selections when closing
      }} maxWidth="md" fullWidth>
        <DialogTitle>Search for Yarn</DialogTitle>
        <DialogContent>
          <YarnCrawler 
            onSelect={handleYarnCrawlerSelect}
            initialBrand={yarnCrawlerSelections?.brand}
            initialYarn={yarnCrawlerSelections?.yarn}
            initialProfile={yarnCrawlerSelections?.profile}
            initialImageUrl={yarnCrawlerSelections?.imageUrl}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowYarnCrawler(false)
            setYarnCrawlerSelections(null) // Clear selections when closing
          }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Yarn Form Dialog */}
      <YarnFormDialog
        open={showYarnForm}
        onClose={() => setShowYarnForm(false)}
        onBack={() => {
          setShowYarnForm(false)
          setShowYarnCrawler(true)
          // Selections are already preserved in yarnCrawlerSelections state
          // Form data will be refreshed when user selects "Use This Yarn" again
        }}
        onSubmit={handleYarnFormSubmit}
        formData={yarnFormData}
        onChange={setYarnFormData}
      />

      {/* Colorway Form Dialog (from main flow) */}
      <ColorwayFormDialog
        open={showColorwayForm}
        onClose={() => {
          setShowColorwayForm(false)
          resetForms()
        }}
        onBack={() => {
          setShowColorwayForm(false)
          setShowYarnForm(true)
        }}
        onSubmit={handleColorwayFormSubmit}
        formData={colorwayFormData}
        onChange={setColorwayFormData}
      />

      {/* Select Existing Colorway Dialog */}
      {selectedYarnForColorway && (
        <SelectColorwayDialog
          open={showSelectColorwayDialog}
          onClose={() => {
            setShowSelectColorwayDialog(false)
            setSelectedYarnForColorway(null)
            // If closing from yarn crawler flow, go back to crawler
            if (yarnCrawlerSelections) {
              setShowYarnCrawler(true)
            }
          }}
          onSelect={handleSelectExistingColorway}
          onCreateNew={handleCreateNewColorway}
          yarnId={selectedYarnForColorway}
          existingColorwayIds={userYarns
            .filter(uy => uy.colorway && uy.colorway.yarn && uy.colorway.yarn.id === selectedYarnForColorway)
            .map(uy => uy.colorway!.id)}
        />
      )}

      {/* Add Colorway Dialog (quick add from existing yarn) */}
      {selectedYarnForColorway && (
        <ColorwayFormDialog
          open={showAddColorwayDialog}
          onClose={() => {
            setShowAddColorwayDialog(false)
            setSelectedYarnForColorway(null)
            setRecentlyCreatedColorwayId(null)
            setColorwayFormData({
              name: '',
              hex_color: '#000000',
              hex_color_locked: false,
              hex_colors: ['#000000']
            })
          }}
          onBack={() => {
            setShowAddColorwayDialog(false)
            setShowSelectColorwayDialog(true)
          }}
          onSubmit={handleAddColorwaySubmit}
          formData={colorwayFormData}
          onChange={setColorwayFormData}
          buttonText="Add Colorway"
        />
      )}

      {/* User Yarn Form Dialog */}
      <UserYarnFormDialog
        open={showUserYarnForm}
        onClose={() => setShowUserYarnForm(false)}
        onBack={() => {
          setShowUserYarnForm(false)
          if (recentlyCreatedColorwayId && selectedYarnForColorway) {
            handleBackToColorwayEdit()
          } else if (selectedYarnForColorway) {
            setShowSelectColorwayDialog(true)
          } else {
            setShowColorwayForm(true)
          }
        }}
        onSubmit={handleUserYarnFormSubmit}
        formData={userYarnFormData}
        onChange={setUserYarnFormData}
      />

    </Box>
  )
}
